import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '../icons/Icons';
import { notificationsService, NotificationItem } from '../../services/notificationsService';

interface Props {
  pollIntervalMs?: number;
  'data-testid'?: string;
}

/**
 * Bell icon with unread badge + dropdown of recent notifications.
 * Polls /unread-count and refreshes on click.
 */
export const NotificationBell: React.FC<Props> = ({
  pollIntervalMs = 30000,
  'data-testid': testId = 'notification-bell',
}) => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const popoverRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const c = await notificationsService.unreadCount();
      setUnread(c);
    } catch {
      /* ignore */
    }
  }, []);

  const loadDropdown = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsService.list({ limit: 5 });
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    if (pollIntervalMs > 0) {
      const t = setInterval(refreshCount, pollIntervalMs);
      return () => clearInterval(t);
    }
  }, [refreshCount, pollIntervalMs]);

  useEffect(() => {
    if (!open) return;
    loadDropdown();
  }, [open, loadDropdown]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleMarkRead = async (id: number) => {
    await notificationsService.markRead(id);
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, read_at: new Date().toISOString() } : x)));
    refreshCount();
  };

  const handleSeeAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        data-testid={testId}
        className="relative w-full min-h-[44px] flex items-center justify-start gap-3 px-3 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-background-surface)] transition-colors"
      >
        <BellIcon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm">Notifications</span>
        {unread > 0 && (
          <span
            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full"
            style={{ backgroundColor: 'var(--color-error, #ef4444)' }}
            data-testid="notification-bell-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 left-0 bottom-full mb-2 w-80 max-h-[420px] overflow-y-auto rounded-lg shadow-lg border"
          style={{
            backgroundColor: 'var(--color-background-card, #fff)',
            borderColor: 'var(--color-border-default, #e5e7eb)',
          }}
          data-testid="notification-bell-popover"
        >
          <div
            className="flex items-center justify-between px-3 py-2 border-b"
            style={{ borderColor: 'var(--color-border-default, #e5e7eb)' }}
          >
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">
              Notifications
            </span>
            <button
              type="button"
              className="text-xs text-[var(--color-text-link)] hover:underline"
              onClick={handleSeeAll}
            >
              See all
            </button>
          </div>

          {loading ? (
            <div className="p-4 text-sm text-[var(--color-text-secondary)]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-[var(--color-text-secondary)]">No notifications</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--color-border-light, #e5e7eb)' }}>
              {items.map((n) => (
                <li
                  key={n.id}
                  className="px-3 py-2 hover:bg-[var(--color-background-surface)] transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: n.color }}
                      aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm ${
                          n.read_at ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-primary)] font-medium'
                        }`}
                      >
                        {n.message}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        {n.trigger_event} · {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                    {!n.read_at && (
                      <button
                        type="button"
                        onClick={() => handleMarkRead(n.id)}
                        className="text-xs text-[var(--color-text-link)] hover:underline flex-shrink-0"
                        data-testid={`notification-mark-read-${n.id}`}
                      >
                        Mark
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
